import {
  DateField,
  FilterDropdownProps,
  Grid,
  List,
  Space,
  Table,
  Tooltip,
  useTable
} from '@pankod/refine-antd'
import { IResourceComponentsProps, useTranslate } from '@pankod/refine-core'
import { EditableSubscriptionStatus } from 'components/EditableSubscriptionStatus/EditableSubscriptionStatus'
import { ExportToExcelButton } from 'components/ExportToExcelButton/ExportToExcelButton'
import { SelectFilterDropdown } from 'components/filterDropdown'
import { definitions } from 'interfaces'
import { DEFAULT_DATE_TIME_FORMAT, formatDateTime, MAX_ROWS_QUERY, supabaseClient } from 'utility'
import { UserSubscriptionOrdersModal } from './userSubscriptionOrdersModal'
import { UserSubscriptions } from './userSubscriptions'

export const SubscribersList: React.FC<IResourceComponentsProps> = () => {
  const selectColumns = 'id, user_id, expired_at, renew_at, users!inner(name, email, addresses!shipping_address(phone_number)),' +
        'subscriptions!inner(name), created_at, status, expired_at, renew_at'

  const { tableProps, tableQueryResult } = useTable<definitions['users_subscriptions']>({
    resource: 'latest_user_subscriptions', // This is a view, not a table
    metaData: {
      select: selectColumns
    },
    permanentFilter: [],
    initialSorter: [
      {
        field: 'created_at',
        order: 'desc'
      }
    ]
  })
  const t = useTranslate()

  const expandedRowRender = (
    usersSubscription: definitions['users_subscriptions']
  ) => {
    return <UserSubscriptions usersSubscription={usersSubscription} />
  }

  const breakpoint = Grid.useBreakpoint()

  const renderStatusCircle = (status: string) => {
    const statusMap: Record<string, { color: string; title: string }> = {
      active: { color: 'limegreen', title: t('subscriptions.statuses.active') },
      cancelled: { color: 'red', title: t('subscriptions.statuses.expired') },
      paused: { color: 'yellow', title: t('subscriptions.statuses.paused') }
    }
    const entry = statusMap[status]
    if (!entry) return status
    return (
      <Tooltip title={entry.title}>
        <span
          style={{
            display: 'inline-block',
            cursor: 'initial',
            width: 12,
            height: 12,
            borderRadius: '50%',
            marginTop: '0.35rem',
            marginRight: '1rem',
            background: entry.color
          }}
        />
      </Tooltip>
    )
  }

  const dataToExport = async () => {
    const { count } = await supabaseClient
      .from('latest_user_subscriptions')
      .select(selectColumns, { count: 'exact', head: true })

    const totalCount = count || 0

    let maxIterations = Math.floor(totalCount / MAX_ROWS_QUERY)
    if (totalCount % MAX_ROWS_QUERY > 0) {
      maxIterations += 1
    }

    const iterateArray = new Array(maxIterations)
    const promiseArray = []
    const range = [0, MAX_ROWS_QUERY - 1]

    // eslint-disable-next-line no-unused-vars
    for (const _ of iterateArray) {
      const query = supabaseClient
        .from('latest_user_subscriptions')
        .select(selectColumns)
        .order('created_at', { ascending: false })
        .range(range[0], range[1])

      promiseArray.push(query)
      range[0] += MAX_ROWS_QUERY
      range[1] += MAX_ROWS_QUERY
    }

    const results = await Promise.all(promiseArray)
    let allData: any[] = []

    results.forEach((r) => {
      if (r.data) {
        allData = [...allData, ...r.data]
      }
    })

    return allData.map((us: any) => {
      const statusMap: Record<string, { color: string; title: string }> = {
        active: { color: 'limegreen', title: t('subscriptions.statuses.active') },
        cancelled: { color: 'red', title: t('subscriptions.statuses.expired') },
        paused: { color: 'yellow', title: t('subscriptions.statuses.paused') }
      }
      const statusTitle = statusMap[us.status]?.title || us.status
      return {
        [t('users.id')]: us.id || '',
        [t('subscriptions.created_at')]: formatDateTime(us.created_at, true) || '',
        [t('users.name')]: us.users?.name || '',
        [t('users.email')]: us.users?.email || '',
        [t('users.phone_number')]: us.users?.addresses?.phone_number || '',
        [t('subscriptions.name')]: us.subscriptions?.name || '',
        [t('subscriptions.status')]: statusTitle || '',
        [t('subscriptions.renew_at')]: formatDateTime(us.renew_at, true) || '',
        [t('subscriptions.expired_at')]: formatDateTime(us.expired_at, true) || ''
      }
    })
  }

  return (
    <List
      title={t('subscriptions.title')}
      breadcrumb={null}
      headerProps={{
        extra: <>
          <ExportToExcelButton filename='Pedidos' asyncFunction={() => dataToExport()} />
        </>
      }}
    >
      <Table
        {...tableProps}
        dataSource={tableProps.dataSource}
        rowKey="id"
        expandable={{
          expandedRowRender: !breakpoint.xs ? expandedRowRender : undefined
        }}
      >
        <Table.Column
          key="id"
          dataIndex="id"
          title={t('users.id')}
          align="center"
          sorter
        />
        <Table.Column
          key="users.id"
          dataIndex={['users', 'name']}
          title={t('users.name')}
          align="center"
          filterDropdown={(props: FilterDropdownProps) => (
            <SelectFilterDropdown
              props={props}
              entity="users"
              optionLabel="name"
              optionValue="id"
            />
          )}
        />
        <Table.Column
          key="users.id"
          dataIndex={['users', 'email']}
          title={t('users.email')}
          align="center"
          filterDropdown={(props: FilterDropdownProps) => (
            <SelectFilterDropdown
              props={props}
              entity="users"
              optionLabel="email"
              optionValue="id"
            />
          )}
        />
        <Table.Column
          key="phone_number"
          dataIndex={['users', 'addresses', 'phone_number']}
          title={t('users.phone_number')}
          align="center"
        />
        <Table.Column
          key="subscriptions.id"
          dataIndex={['subscriptions', 'name']}
          title={t('subscriptions.name')}
          align="center"
          filterDropdown={(props: FilterDropdownProps) => (
            <SelectFilterDropdown
              props={props}
              entity="subscriptions"
              optionLabel="name"
              optionValue="id"
            />
          )}
        />
        <Table.Column
          key="status"
          dataIndex={['status']}
          title={t('subscriptions.status')}
          width={25}
          render={(value, record: definitions['users_subscriptions']) => {
            return <div style={{ display: 'flex', justifyContent: 'left', alignItems: 'center' }}>
              {renderStatusCircle(value)}
              <EditableSubscriptionStatus key={t(`subscription_status.select.${value}`)} record={record} refetchFn={tableQueryResult.refetch} />
            </div>
          }}
          align="center"
          sorter
        />
        <Table.Column
          key="renew_at"
          dataIndex={['renew_at']}
          title={t('subscriptions.renew_at')}
          width={25}
          render={(value) => (value) ? <DateField format={DEFAULT_DATE_TIME_FORMAT} value={value}/> : t('subscriptions.no_renew')}
          align="center"
          sorter
        />
        <Table.Column
          key="expired_at"
          dataIndex={['expired_at']}
          title={t('subscriptions.expired_at')}
          width={25}
          render={(value) => (value) ? <DateField format={DEFAULT_DATE_TIME_FORMAT} value={value}/> : ''}
          align="center"
          sorter
        />
        <Table.Column<definitions['users_subscriptions']>
          title={t('table.actions')}
          dataIndex="actions"
          render={(_, record) => {
            return (
              <Space>
                <UserSubscriptionOrdersModal userId={record.user_id} />
              </Space>
            )
          }}
        />
      </Table>
    </List>
  )
}
